// src/components/CustomSideBar.tsx
import React from 'react';
import { useNavigate, useLocation } from "react-router-dom";
import { AppButton } from '.';
import { Box, List, ListItem, Link, IconButton, Collapse, Tooltip } from "@mui/material";
import { styled } from '@mui/material/styles';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { useAuth } from '../hooks/useAuth';
import { useSelector } from 'react-redux';
import { Agent, SidebarItem } from '../types';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import { RootState } from '../stores';
import { useGlobalContext } from '../context/GlobalContext';
import { openDialerForUser } from '../utils/dialer';

const SidebarContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexFlow: 'column',
  width: '248px',
  backgroundColor: '#002F73',
  boxShadow: '0px 0px 4px 0px rgba(0, 0, 0, 0.25)',
  maxHeight: '100%',
  // Three fixed zones: logo on top, scrollable menu in the middle, logout pinned
  // at the bottom — only the menu list scrolls, not the whole rail.
  overflow: 'hidden',
  transition: 'width 300ms',
  '&.smallSidebar': {
    width: 74,
    '& .MuiButtonBase-root': {
      margin: 0,
      justifyContent: 'center',
    },
    '& .sidebarLogo': {
      paddingLeft: 0,
      paddingRight: 0,
      justifyContent: 'center',
      '& .logo': {
        display: 'none',
      },
    },
    '& .MuiListItem-root': {
      '& .MuiListItemIcon-root': {
        margin: 0,
      },
      '& .MuiListItemText-root': {
        display: 'none',
      }
    },
    '& .logBtn': {
      paddingLeft: 0,
      paddingRight: 0,
      '& .MuiButtonBase-root': {
        fontSize: 0,
        minWidth: 1,
        padding: theme.spacing(1.25),
        '& .MuiButton-icon': {
          margin: 0,
        },
      },
    },
    '& + .MuiBox-root': {
      maxWidth: 'calc(100% - 74px)',
    }
  }
}));

const SidebarList = styled(List)(() => ({
  padding: 0,
  width: '100%',
}));

// The only scrollable zone, with a slim scrollbar (tinted for the dark theme).
const SidebarMenuArea = styled(Box)(() => ({
  flex: 1,
  minHeight: 0,
  width: '100%',
  overflowY: 'auto',
  overflowX: 'hidden',
  scrollbarWidth: 'thin',
  scrollbarColor: '#3F6BB0 transparent',
  '&::-webkit-scrollbar': { width: 6 },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': { backgroundColor: '#3F6BB0', borderRadius: 8 },
  '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#5A82C4' },
}));

const SidebarListItemButton = styled(ListItemButton)(({ theme }) => ({
  padding: theme.spacing(2.25, 2.5),
  color: '#fff',
  transition: '500ms',
  position: 'relative',
  '& .MuiListItemIcon-root': {
    filter: 'brightness(0) invert(1)',
    opacity: 1,
  },
  '&.no-filter .MuiListItemIcon-root': {
    filter: 'none',
  },
  '&:hover': {
    backgroundColor: '#2B62B1',
  },
  '&.selected': {
    backgroundColor: '#2B62B1',
    // Left accent on the active item, mirroring the supervisor rail.
    boxShadow: 'inset 3px 0 0 #9EC1FF',
  },
}));

const SidebarListItemIcon = styled(ListItemIcon)(({ theme }) => ({
  width: 24,
  minWidth: 24,
  filter: 'grayscale(1)',
  opacity: 0.6,
  transition: '500ms',
  marginRight: theme.spacing(1.25),
}));

const SidebarListItemText = styled(ListItemText)(() => ({
  margin: 0,
  // padding: 0,
  // '& .MuiListItemText-primary': {
  //   fontSize: 17,
  //   lineHeight: '23px',
  //   fontWeight: 400,
  //   transition: '500ms',
  // },
}));

const NavLogoutBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  flexShrink: 0,
  padding: theme.spacing(2),
  marginTop: 'auto',
  borderTop: '1px solid rgba(255, 255, 255, 0.12)',
}));

const SidebarLogo = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  flexShrink: 0,
  padding: theme.spacing(1, 2.5),
  minHeight: 74,
  backgroundColor: theme.palette.primary.main,
}));

const CustomSideBar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user: Agent | null = useAuth();
  const token = useSelector((state: RootState) => state.auth.accessToken);
  const [open, setOpen] = React.useState('');
  const [isExpanded, setIsExpanded] = React.useState(false);
  const { handleLogout } = useGlobalContext();
  const sidebarToggle = () => {
    setIsExpanded((prev) => !prev);
  };

  const isActive = (path?: string) => {
    if (!path) return false;
    return location.pathname === path;
  };

  const redirectToDialer = () => openDialerForUser(user, token);

  const handleToggle = (label: string) => setOpen(prev => label === prev ? '' : label);


  const menuItems: SidebarItem[] = [
    { label: "Dashboard", icon: "/images/dashboard-icon.svg", path: "/dashboard" },
    {
      label: "Tickets Feature",
      icon: "/images/tickets-icon.svg",
      children: [
        { label: "Tickets", icon: "/images/group-ticket.svg", path: "/tickets" },
        { label: "Categories", icon: "/images/ticket-logs.svg", path: "/ticket-categories" },
        { label: "Sub Categories", icon: "/images/ticket-sub.svg", path: "/ticket-sub-categories" },
        { label: "Sub-Sub Categories", icon: "/images/ticket-sub-sub.svg", path: "/ticket-sub-sub-categories" },
      ],
    },
    { label: "Dialer", icon: "/images/dialer-icon.svg", onClick: redirectToDialer },
    { label: "General Enquiry", icon: "/images/customer-inquiry-icon.svg", path: "/customer-query" },
    { label: "Scripts", icon: "/images/text-icon.svg", path: "/scripts" },
    { label: "Attendance", icon: "/images/calendar-icon.svg", path: "/attendance" },
    {
      label: "CRM", icon: "/images/crm-icon.svg",
      children: [
        { label: "Customer", icon: "/images/work-force-icon.svg", path: "/crm/customers" },
      ]
    },
    {
      label: "Sales Pipeline Feature", icon: "/images/sales-pipeline-icon.svg",
      children: [
        { label: "Sales", icon: "/images/sales-icon.svg", path: "/crm/sales" },
        { label: "Leads", icon: "/images/leads-icon2.svg", path: "/crm/leads" },
        { label: "Sales Enquiry", icon: "/images/sales-query.svg", path: "/crm/sales-query" },
      ]
    },
    {
      label: "Marketing", icon: "/images/marketing-icon.svg",
      children: [
        { label: "Email Templates", icon: "/images/email-solid-icon.svg", path: "/email/templates" },
        { label: "Prospects", icon: "/images/prospects-icon.svg", path: "/prospects" },
        { label: "Segments", icon: "/images/segments-icon.svg", path: "/segments" },
        { label: "Campaigns", icon: "/images/email-campaign-icon.svg", path: "/campaigns" },
      ]
    },
    {
      label: "Shift & Schedule", icon: "/images/work-force-icon.svg",
      children: [
        { label: "Work Schedule", icon: "/images/work-shift-icon.svg", path: "/work-schedule" },
        { label: "Shift Swaps", icon: "/images/crm-icon.svg", path: "/shift-swaps" },
        { label: "Leave Requests", icon: "/images/leave-request-icon.svg", path: "/leave-requests" },
      ]
    },
    {
      label: "Facebook",
      icon: "/images/facebook-icon.svg",
      children: [
        { label: "Conversations", icon: "/images/chatbot-icon.svg", path: "/facebook-conversations" },
        { label: "Post Threads", icon: "/images/facebook-icon.svg", path: "/facebook-post-threads" },
        // { label: "Templates", icon: "/images/facebook-icon.svg", path: "/facebook-templates" },
      ],
    },
    {
      label: "Instagram",
      icon: "/images/instagram.svg",
      children: [
        { label: "Conversations", icon: "/images/chatbot-icon.svg", path: "/instagram-conversations" },
        { label: "Post Threads", icon: "/images/instagram.svg", path: "/instagram-post-threads" },
        // { label: "Templates", icon: "/images/instagram.svg", path: "/instagram-templates" },
      ],
    },
    {
      label: "WhatsApp",
      icon: "/images/whatsapp-icon.svg",
      children: [
        { label: "Conversations", icon: "/images/chatbot-icon.svg", path: "/whatsapp-conversations" },
        // { label: "Setup", icon: "/images/whatsapp-icon.svg", path: "/settings/whatsapp" },
      ],
    },
  ];

  const filterRoutesByMenu = (): SidebarItem[] => {
    const assignMenuRaw = user?.assignMenu as { menu: { menuName: string } }[] | undefined;
    const originalMenuRaw = user?.originalMenu as unknown as { menu: { menuName: string } }[] | undefined;

    // Mapping from API menu name → sidebar route path(s) used in menuItems.
    // Gating keys on path because paths are unique across the sidebar, while
    // labels like "Conversations" / "Post Threads" / "Templates" repeat per
    // platform (Facebook / Instagram / WhatsApp) and would cross-grant items.
    // Items without a path (e.g. Dialer) are keyed by their label instead.
    // One backend entry can gate multiple sidebar items (e.g. "Ticket Sub Categories"
    // covers both Categories and Sub Categories, mirroring the tenant's shared menu id).
    const menuNameMapping: Record<string, string | string[]> = {
      "Dashboard": "/dashboard",
      "Tickets": "/tickets",
      "Ticket Categories": "/ticket-categories",
      // Menu id 47 gates Categories, Sub Categories and Sub-Sub Categories together.
      "Ticket Sub Categories": ["/ticket-categories", "/ticket-sub-categories", "/ticket-sub-sub-categories"],
      "Dialer": "Dialer",
      "Customer inquiry": "/customer-query",
      "Script": "/scripts",
      "CRM Customer": "/crm/customers",
      "CRM Sales": "/crm/sales",
      "CRM Leads": "/crm/leads",
      "AI Assistant": "AI Assistant",
      "Performance": "Performance",
      "Attendance": "/attendance",
      "Sales Query": "/crm/sales-query",
      "Work Schedule": "/work-schedule",
      "Shift Swap": "/shift-swaps",
      "Leave Request": "/leave-requests",
      "Marketing Email Templates": "/email/templates",
      "Marketing Campaigns": "/campaigns",
      "Marketing Segments": "/segments",
      "Marketing Prospects": "/prospects",
      // Facebook backend menu (id 49) gates Conversations, Post Threads and Templates children.
      "Facebook Post Threads ": ["/facebook-conversations", "/facebook-post-threads", "/facebook-templates"],
      "Facebook Post Threads": ["/facebook-conversations", "/facebook-post-threads", "/facebook-templates"],
      // Instagram backend menu (id 52) gates Conversations, Post Threads and Templates children.
      "Instagram Post Threads ": ["/instagram-conversations", "/instagram-post-threads", "/instagram-templates"],
      "Instagram Post Threads": ["/instagram-conversations", "/instagram-post-threads", "/instagram-templates"],
      "Instagram": ["/instagram-conversations", "/instagram-post-threads", "/instagram-templates"],
      // WhatsApp menu (id 37) — backend menuName is "Settings WhatsApp"; gates both children.
      "Settings WhatsApp": ["/whatsapp-conversations", "/settings/whatsapp"],
    };

    // Key used to match a sidebar item against the granted keys: path when
    // present (unique), label otherwise (path-less items like Dialer).
    const itemKey = (item: { path?: string; label: string }): string => item.path ?? item.label;

    const toKeys = (list?: { menu: { menuName: string } }[]): string[] =>
      (list ?? []).flatMap((item) => {
        if (!item || typeof item !== 'object' || !('menu' in item) || !item.menu || !('menuName' in item.menu)) return [];
        const mapped = menuNameMapping[item.menu.menuName];
        if (!mapped) return [];
        return Array.isArray(mapped) ? mapped : [mapped];
      });

    // Fallback path: no originalMenu in response → keep prior behaviour (filter out unassigned)
    if (!originalMenuRaw || originalMenuRaw.length === 0) {
      if (!assignMenuRaw || assignMenuRaw.length === 0) return menuItems;
      const allowedKeys = toKeys(assignMenuRaw);
      return menuItems
        .map((item) => {
          if (item.children) {
            const filteredChildren = item.children.filter((c) => allowedKeys.includes(itemKey(c)));
            if (filteredChildren.length === 0) return null;
            return { ...item, children: filteredChildren };
          }
          return allowedKeys.includes(itemKey(item)) ? item : null;
        })
        .filter(Boolean) as SidebarItem[];
    }

    const originalKeys = toKeys(originalMenuRaw);
    const assignedKeys = toKeys(assignMenuRaw);

    return menuItems
      .map((item) => {
        if (item.children) {
          const visibleChildren = item.children
            .filter((c) => originalKeys.includes(itemKey(c)))
            .map((c) => ({ ...c, disabled: !assignedKeys.includes(itemKey(c)) }));
          if (visibleChildren.length === 0) return null;
          const allDisabled = visibleChildren.every((c) => c.disabled);
          return { ...item, children: visibleChildren, disabled: allDisabled };
        }
        if (!originalKeys.includes(itemKey(item))) return null;
        return { ...item, disabled: !assignedKeys.includes(itemKey(item)) };
      })
      .filter(Boolean) as SidebarItem[];
  };


  const filteredMenuItems = filterRoutesByMenu();

  return (
    <SidebarContainer className={isExpanded ? 'smallSidebar' : ''}>
      <SidebarLogo className="sidebarLogo" sx={{ gap: "5px" }}>
        <Link
          className="logo"
          onClick={() => navigate('/')}
          sx={{ display: 'inline-flex', '& img': { maxWidth: '100%', filter: 'brightness(0) invert(1)' } }}
        >
          <img src="/images/logo-2x.png" alt="Logo" />
        </Link>
        <IconButton sx={{ ml: 'auto', mt: 1.5, mr: -0.75 }} onClick={sidebarToggle} aria-label="Toggle sidebar">
          {isExpanded ? <MenuIcon sx={{ color: '#fff' }} /> : <MenuOpenIcon sx={{ color: '#fff' }} />}
        </IconButton>
      </SidebarLogo>
      <SidebarMenuArea as="nav" aria-label="main mailbox folders" className="dashNavbar">
        <SidebarList>
          {filteredMenuItems.map((item, index) => {
            const hasChildren = Number(item.children?.length) > 0;
            const parentClickDisabled = !hasChildren && item.disabled;
            const isNoFilter = item.label === 'Facebook' || item.label === 'Instagram';
            const parentButton = (
              <SidebarListItemButton
                onClick={() => {
                  if (parentClickDisabled) return;
                  if (hasChildren) {
                    handleToggle(item.label);
                  } else if (item?.onClick) item?.onClick()
                  else if (item.path) {
                    navigate(item.path);
                  }
                }}
                className={`${isActive(item.path) ? "selected" : ""} ${isNoFilter ? "no-filter" : ""}`}
                sx={parentClickDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
              >
                <SidebarListItemIcon>
                  <img src={item.icon} alt={item.label} />
                </SidebarListItemIcon>
                <SidebarListItemText primary={item.label} />
                {hasChildren && (open ? <ExpandLess /> : <ExpandMore />)}
              </SidebarListItemButton>
            );

            return (
              <React.Fragment key={index}>
                <ListItem disablePadding sx={{ mt: 1, ...(hasChildren ? { flexWrap: 'wrap' } : {}) }}>
                  {parentClickDisabled ? (
                    <Tooltip title="Contact admin for access" placement="right" arrow>
                      <Box sx={{ width: '100%' }}>{parentButton}</Box>
                    </Tooltip>
                  ) : (
                    parentButton
                  )}
                </ListItem>
                {hasChildren && (
                  <Collapse in={open === item.label} timeout="auto" unmountOnExit sx={{ width: '100%' }}>
                    <SidebarList disablePadding>
                      {item.children?.map((child, idx) => {
                        const isChildNoFilter = child.icon.includes('facebook') || child.icon.includes('instagram');
                        const childButton = (
                          <SidebarListItemButton
                            sx={{ pl: 6, pr: 2.5, py: 1.5, ...(child.disabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}) }}
                            onClick={() => {
                              if (child.disabled) return;
                              if (child?.onClick) child?.onClick()
                              else if (child?.target) window.open(child?.path, child?.target);
                              else if (child.path) navigate(child.path)
                            }}
                            className={`${isActive(child.path) ? "selected" : ""} ${isChildNoFilter ? "no-filter" : ""}`}
                          >
                            <SidebarListItemIcon>
                              <img src={child.icon} alt={child.label} />
                            </SidebarListItemIcon>
                            <SidebarListItemText primary={child.label} />
                          </SidebarListItemButton>
                        );
                        return (
                          <ListItem disablePadding sx={{ mt: 1 }} key={idx}>
                            {child.disabled ? (
                              <Tooltip title="Contact admin for access" placement="right" arrow>
                                <Box sx={{ width: '100%' }}>{childButton}</Box>
                              </Tooltip>
                            ) : (
                              childButton
                            )}
                          </ListItem>
                        );
                      })}
                    </SidebarList>
                  </Collapse>
                )}
              </React.Fragment>
            );
          })}
        </SidebarList>
      </SidebarMenuArea>

      <NavLogoutBox className="logBtn">
        <AppButton
          className="light"
          onClick={handleLogout}
          type="button"
          disableElevation
          variant="outlined"
          size="small"
          startIcon={<img src="/images/logout.svg" alt="Logout" />}
        >
          Logout
        </AppButton>
      </NavLogoutBox>
    </SidebarContainer>
  );
};

export default CustomSideBar;
